/**
 * AUTHENTICATION SERVICE
 *
 * New secure authentication service with refresh token support
 * SECURITY: Handles JWT tokens, auto-refresh, and secure logout
 */

import axios, { AxiosResponse, AxiosError } from "axios";
import Cookies from "js-cookie";
import { notification } from "antd";
import i18n from "../i18n";

export const API_PATH = window.location.host === "test-klient.proste.menu"
  ? "https://test-server.proste.menu"
  : (process.env.REACT_APP_API_URL || "http://localhost:8000");

// Create separate axios instance for auth (to avoid circular interceptor issues)
const authAxios = axios.create({
  baseURL: API_PATH,
  withCredentials: true,
});

// Track if we're currently refreshing to avoid multiple refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

/**
 * SECURITY: Response interceptor to handle token refresh
 */
authAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Skip auto-refresh for login/register requests
    if (originalRequest.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return authAxios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = Cookies.get("refreshToken");

      if (!refreshToken) {
        // No refresh token, logout
        handleLogout();
        return Promise.reject(error);
      }

      try {
        // Try to refresh the access token
        const response = await axios.post(`${API_PATH}/v1/common_api/auth/refresh-token`, {
          refreshToken,
        });

        const { accessToken } = response.data;

        // Store new access token
        Cookies.set("accessToken", accessToken, { expires: 7 }); // 15 minutes

        // Update auth header
        authAxios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue();

        // Retry original request
        return authAxios(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * SECURITY: Request interceptor to attach access token and impersonation header
 */
authAxios.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get("accessToken");
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    // IMPERSONATION: Add X-Impersonate-Company header if developer is impersonating
    const role = Cookies.get("role");
    const impersonatedCompanyId = Cookies.get("impersonatedCompanyId");

    if (role === "DEVELOPER" && impersonatedCompanyId) {
      config.headers["X-Impersonate-Company"] = impersonatedCompanyId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handle logout - clear tokens and redirect
 */
let isHandlingLogout = false;
const handleLogout = () => {
  if (isHandlingLogout) return;
  isHandlingLogout = true;

  Cookies.remove("accessToken", { path: '/' });
  Cookies.remove("refreshToken", { path: '/' });
  Cookies.remove("role", { path: '/' });

  notification.warning({
    message: i18n.t("authService.sessionExpired"),
    description: i18n.t("authService.loginAgain"),
    placement: "bottomRight",
  });

  // Reload to show login form
  setTimeout(() => {
    window.location.reload();
  }, 1500);
};

/**
 * REGISTER - Create new account
 * POST /v1/common_api/auth/register
 */
export const register = async (data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  phoneNumber?: string;
  claimToken?: string;
}): Promise<any> => {
  const response: AxiosResponse = await authAxios.post("/v1/common_api/auth/register", data, {
    skipAuthRefresh: true,
  } as any);
  return response.data;
};

/**
 * LOGIN - Authenticate user OR developer with automatic fallback
 * POST /v1/common_api/auth/login (tries User first)
 * POST /v1/common_api/auth/developer/login (fallback if User login fails)
 * SECURITY: Stores both access and refresh tokens
 *
 * Flow:
 * 1. Try logging in as regular User
 * 2. If 401 (invalid credentials for User), try as Developer
 * 3. Store appropriate tokens and role
 */
export const loginNew = async (data: {
  email: string;
  password: string;
}): Promise<any> => {
  try {
    const response: AxiosResponse = await authAxios.post("/v1/common_api/auth/login", data, {
      skipAuthRefresh: true,
    } as any);
    const { accessToken, refreshToken, role, user } = response.data;

    Cookies.set("accessToken", accessToken, { expires: 7, path: '/' });
    Cookies.set("refreshToken", refreshToken, { expires: 30, path: '/' });
    Cookies.set("role", role, { path: '/' });

    return { user, role };
  } catch (userError: any) {

    // STEP 2: If user login failed with 401, try developer login
    if (userError.response?.status === 401) {
      try {
        const devResponse: AxiosResponse = await authAxios.post("/v1/common_api/auth/developer/login", data, {
          skipAuthRefresh: true,
        } as any);
        const { accessToken, refreshToken, role, developer } = devResponse.data;

        Cookies.set("accessToken", accessToken, { expires: 7, path: '/' });
        Cookies.set("refreshToken", refreshToken, { expires: 30, path: '/' });
        Cookies.set("role", role, { path: '/' });

        return { user: developer, role };
      } catch (devError) {
        throw userError;
      }
    }

    // If error is not 401, throw immediately (e.g., network error, 500, etc.)
    throw userError;
  }
};

/**
 * LOGOUT - Invalidate refresh token
 * POST /v1/common_api/auth/logout
 */
export const logoutNew = async (): Promise<void> => {
  const refreshToken = Cookies.get("refreshToken");

  try {
    await authAxios.post("/v1/common_api/auth/logout", { refreshToken });
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    // Clear tokens regardless of API response
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    Cookies.remove("role");
  }
};

/**
 * VERIFY EMAIL - Confirm email address
 * POST /v1/common_api/auth/verify-email
 */
export const verifyEmail = async (token: string): Promise<any> => {
  const response: AxiosResponse = await authAxios.post("/v1/common_api/auth/verify-email", {
    token,
  });

  const { accessToken, refreshToken, user } = response.data;

  // Store tokens after verification
  if (accessToken && refreshToken) {
    Cookies.set("accessToken", accessToken, { expires: 7 });
    Cookies.set("refreshToken", refreshToken, { expires: 30 });
    Cookies.set("role", user.role);
  }

  return response.data;
};

/**
 * REQUEST PASSWORD RESET - Send reset email
 * POST /v1/common_api/auth/request-password-reset
 */
export const requestPasswordReset = async (email: string): Promise<any> => {
  const response: AxiosResponse = await authAxios.post(
    "/v1/common_api/auth/request-password-reset",
    { email }
  );
  return response.data;
};

/**
 * CONFIRM PASSWORD RESET - Set new password
 * POST /v1/common_api/auth/confirm-password-reset
 */
export const confirmPasswordReset = async (data: {
  token: string;
  newPassword: string;
}): Promise<any> => {
  const response: AxiosResponse = await authAxios.post(
    "/v1/common_api/auth/confirm-password-reset",
    data
  );
  return response.data;
};

/**
 * CHANGE PASSWORD - Update password (requires current password)
 * POST /v1/common_api/auth/change-password
 * SECURITY: Requires authentication
 */
export const changePassword = async (data: {
  currentPassword: string;
  newPassword: string;
}): Promise<any> => {
  const refreshToken = Cookies.get("refreshToken");

  const response: AxiosResponse = await authAxios.post("/v1/common_api/auth/change-password", {
    ...data,
    refreshToken, // To keep current session active
  });

  return response.data;
};

/**
 * RESEND VERIFICATION EMAIL
 * POST /v1/common_api/auth/resend-verification
 */
export const resendVerificationEmail = async (email: string): Promise<any> => {
  const response: AxiosResponse = await authAxios.post(
    "/v1/common_api/auth/resend-verification",
    { email }
  );
  return response.data;
};

/**
 * DEVELOPER LOGIN - Authenticate developer
 * POST /v1/common_api/auth/developer/login
 * SECURITY: Stores both access and refresh tokens with DEVELOPER role
 */
export const loginDeveloper = async (data: {
  email: string;
  password: string;
}): Promise<any> => {
  const response: AxiosResponse = await authAxios.post("/v1/common_api/auth/developer/login", data);

  const { accessToken, refreshToken, role, developer } = response.data;

  // SECURITY: Store tokens in cookies
  Cookies.set("accessToken", accessToken, { expires: 7 });
  Cookies.set("refreshToken", refreshToken, { expires: 30 });
  Cookies.set("role", role); // Will be 'DEVELOPER'

  return { developer, role };
};

/**
 * DEVELOPER LOGOUT - Invalidate developer refresh token
 * POST /v1/common_api/auth/developer/logout
 */
export const logoutDeveloper = async (): Promise<void> => {
  const refreshToken = Cookies.get("refreshToken");

  try {
    await authAxios.post("/v1/common_api/auth/developer/logout", { refreshToken });
  } catch (error) {
    console.error("Developer logout error:", error);
  } finally {
    // Clear tokens regardless of API response
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    Cookies.remove("role");
    Cookies.remove("impersonatedCompanyId", { path: '/' }); // Clear impersonation state
    Cookies.remove("impersonatedCompanyName", { path: '/' });
  }
};

/**
 * DEVELOPER REFRESH TOKEN
 * POST /v1/common_api/auth/developer/refresh-token
 */
export const refreshDeveloperToken = async (): Promise<any> => {
  const refreshToken = Cookies.get("refreshToken");

  const response: AxiosResponse = await authAxios.post(
    "/v1/common_api/auth/developer/refresh-token",
    { refreshToken }
  );

  const { accessToken } = response.data;

  // Store new access token
  Cookies.set("accessToken", accessToken, { expires: 7 });

  return response.data;
};

/**
 * GET CURRENT DEVELOPER INFO
 * GET /v1/common_api/auth/developer/me
 */
export const getCurrentDeveloper = async (): Promise<any> => {
  const response: AxiosResponse = await authAxios.get("/v1/common_api/auth/developer/me");
  return response.data;
};

/**
 * SEARCH COMPANIES (Developer only)
 * GET /v1/common_api/developers/companies/search?q=...
 */
export const searchCompanies = async (query: string): Promise<any> => {
  const response: AxiosResponse = await authAxios.get(
    `/v1/common_api/developers/companies/search?q=${encodeURIComponent(query)}`
  );
  return response.data;
};

/**
 * GET ALL USERS (Developer only)
 * GET /v1/common_api/developers/users
 */
export const getAllUsers = async (page: number = 1, limit: number = 20): Promise<any> => {
  const response: AxiosResponse = await authAxios.get(
    `/v1/common_api/developers/users?page=${page}&limit=${limit}`
  );
  return response.data;
};

/**
 * Export axios instance for other API calls
 */
export { authAxios };
